import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, Plus, PanelRightOpen, PanelRightClose, Trash2 } from 'lucide-react';

export default function ConversationPanel({
  conversations,
  activeId,
  onSelect,
  onNew,
  onDelete,
  open,
  onToggle,
}) {
  return (
    <>
      {/* Toggle button when collapsed */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            onClick={onToggle}
            className="fixed right-3 top-1/2 -translate-y-1/2 z-30 p-2.5 rounded-xl
                       bg-white/80 backdrop-blur-xl border border-white/40 shadow-lg
                       hover:bg-white hover:shadow-xl transition-all duration-300
                       text-gray-500 hover:text-gray-700"
            title="打开对话列表"
          >
            <PanelRightOpen size={18} />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 280, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 380, damping: 32 }}
            className="h-full flex-shrink-0 overflow-hidden relative z-20"
          >
            <div className="h-full w-[280px] flex flex-col
                          bg-white/70 backdrop-blur-xl
                          border-l border-white/30
                          shadow-2xl shadow-black/5
                          relative
                          before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/40 before:to-white/10 before:pointer-events-none">

              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200/50 relative z-10">
                <span className="text-sm font-medium text-gray-700">对话</span>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={onNew}
                    className="p-1.5 hover:bg-gray-200/60 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                    title="新建对话"
                  >
                    <Plus size={16} />
                  </button>
                  <button
                    onClick={onToggle}
                    className="p-1.5 hover:bg-gray-200/60 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
                    title="收起面板"
                  >
                    <PanelRightClose size={16} />
                  </button>
                </div>
              </div>

              {/* Conversation list */}
              <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1 relative z-10">
                {conversations.length === 0 ? (
                  <div className="text-center text-gray-400 text-xs py-8">
                    暂无对话，点 + 开始
                  </div>
                ) : (
                  conversations
                    .slice()
                    .sort((a, b) => b.updatedAt - a.updatedAt)
                    .map((conv) => {
                      const isActive = conv.id === activeId;
                      const preview = conv.messages.length > 0
                        ? conv.messages[0].content.slice(0, 40)
                        : '新对话';

                      return (
                        <motion.div
                          key={conv.id}
                          layout
                          initial={{ opacity: 0, y: -8 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`group relative flex items-start space-x-2 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 ${
                            isActive
                              ? 'bg-gray-900/8 shadow-sm'
                              : 'hover:bg-gray-200/40'
                          }`}
                          onClick={() => onSelect(conv.id)}
                        >
                          <MessageSquare
                            size={14}
                            className={`mt-0.5 flex-shrink-0 ${
                              isActive ? 'text-gray-700' : 'text-gray-400'
                            }`}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium text-gray-700 truncate">
                              {conv.title || preview}
                            </div>
                            <div className="text-[10px] text-gray-400 mt-0.5">
                              {conv.messages.length} 条消息 · {formatTime(conv.updatedAt)}
                            </div>
                          </div>
                          {conversations.length > 1 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onDelete(conv.id);
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-all text-gray-400 hover:text-red-500"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </motion.div>
                      );
                    })
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function formatTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if (diff < 60_000) return '刚刚';
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)} 分钟前`;
  if (diff < 86400_000) return `${Math.floor(diff / 3600_000)} 小时前`;
  return `${d.getMonth() + 1}/${d.getDate()}`;
}
